// ABOUTME: Confirmation dialog — the question is the title, the answer is two buttons
// ABOUTME: Sits on CModalDialog, so it shares the system's dialog shell (DESIGN_SYSTEM §4.7)

import * as React from "react"
import { CModalDialog } from "./CModalDialog"
import { CButton } from "./CButton"
import { useTranslation } from "react-i18next"

interface CConfirmModalProps {
  isShow: boolean
  onClose: () => void
  onSuccess: () => void
  isProcessing?: boolean
  /** The confirm alone is shut — not yet answerable — while cancel and "don't save" stay open, unlike isProcessing. */
  isConfirmDisabled?: boolean
  /** Heading. Without one the description becomes the heading — a question needs no preamble. */
  title?: string
  /** Explanation under the title; on its own it *is* the title. */
  description?: string
  /** Custom confirm button text (default: "Да") */
  confirmText?: string
  /** Custom cancel button text (default: "Отмена") */
  cancelText?: string
  /** Confirm button color (default: "blue") */
  confirmColor?: "blue" | "red" | "gray" | "light"
  /** Custom content instead of description */
  children?: React.ReactNode
  /** Show "Don't Save" button for unsaved changes dialogs */
  showDontSave?: boolean
  /** Callback when "Don't Save" is clicked */
  onDontSave?: () => void
  /** Custom "Don't Save" button text (default: "Не сохранять") */
  dontSaveText?: string
}

const BUTTON_CLASS = "font-medium max-md:min-h-11 max-md:w-full"

export const CConfirmModal: React.FC<CConfirmModalProps> = (props) => {
  const { t } = useTranslation()
  const {
    isShow,
    onClose,
    onSuccess,
    isProcessing = false,
    isConfirmDisabled = false,
    // Empty strings count as absent: callers that have no title or no message
    // pass "" rather than omitting the prop (useLocalConfirmation does), and
    // `??` would keep it — leaving a dialog with nothing in it.
    title: titleProp,
    description: descriptionProp,
    confirmText,
    cancelText,
    confirmColor = "blue",
    children,
    showDontSave,
    onDontSave,
    dontSaveText,
  } = props

  const title = titleProp || undefined
  const description = descriptionProp || undefined

  // «Вы уверены, что хотите удалить?» is the whole dialog: it belongs in the
  // header, not as a second heading below an empty one. A caller that supplies
  // both keeps the description as the body text under the title.
  const heading = title ?? (children ? t("common.confirmation") : (description ?? t("common.confirmText")))
  // Only an explicit description earns a body — the default question is the
  // heading, and repeating it under a caller's own title would be noise.
  const bodyText = children ?? (title && description ? description : null)

  return (
    <CModalDialog
      isOpen={isShow}
      onClose={onClose}
      title={heading}
      width={showDontSave ? 460 : 420}
      isSubmitting={isProcessing}
      footer={
        // Cancel first on desktop, last on a phone — the primary answer is
        // where the thumb already is (CModalDialog does the same).
        <>
          <CButton
            color="gray"
            size="md"
            onClick={onClose}
            disabled={isProcessing}
            label={cancelText || t("common.cancel")}
            className={`${BUTTON_CLASS} max-md:order-3`}
            showLabelOnMobile
          />
          {showDontSave && (
            <CButton
              color="light"
              size="md"
              onClick={onDontSave}
              disabled={isProcessing}
              label={dontSaveText || t("common.dontSave")}
              className={`${BUTTON_CLASS} max-md:order-2`}
              showLabelOnMobile
            />
          )}
          <CButton
            color={confirmColor}
            size="md"
            onClick={onSuccess}
            disabled={isProcessing || isConfirmDisabled}
            isProcessing={isProcessing}
            label={confirmText || t("common.yes")}
            className={`${BUTTON_CLASS} font-semibold`}
            showLabelOnMobile
          />
        </>
      }
    >
      {bodyText === null ? undefined : (
        <div className="text-sm break-words whitespace-pre-line text-gray-600">{bodyText}</div>
      )}
    </CModalDialog>
  )
}
